import React, { Dispatch, SetStateAction } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";

import { JiraTemplateChoice } from "../../../hooks";
import { UserSegmentationTeamTypes } from "../../../models/user-segmentation";
import { TemplateOptionMetadata } from "../team-type-template-map";

interface CardProps {
    imageSrc: string;
    mobileImgSrc: string;
    title: string;
    subtext: string;
    templateType: JiraTemplateChoice;
    templateId: string;
    setSelectedTemplate: Dispatch<SetStateAction<JiraTemplateChoice>>;
    setSelectedTemplateId: Dispatch<SetStateAction<string | undefined>>;
    selectedCardTitle: string | undefined;
    setSelectedCardTitle: Dispatch<SetStateAction<string | undefined>>;
    teamType: UserSegmentationTeamTypes;
    templateMetadata?: TemplateOptionMetadata;
    setSelectedTemplateMetadata: Dispatch<
        SetStateAction<TemplateOptionMetadata | undefined>
    >;
}

const cardBorderStyle = css({
    margin: "1px",
    padding: "0px",
    borderRadius: "var(--rounding, 8px)",
    border: "1px solid #C1C7D0",
    background: "#FFF",
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    maxWidth: "463px",
    minWidth: "294px",
    alignItems: "center",
    cursor: "pointer",
    ["&:hover"]: {
        boxShadow:
            "0px 1px 1px 0px rgba(9, 30, 66, 0.25), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)"
    },
    [media.above.md]: {
        flexDirection: "column",
        justifyContent: "flex-start",
        maxWidth: "none",
        minHeight: "333px",
        flex: 1
    }
});

const unselectedButtonStyle = css({
    borderColor: token("color.background.accent.gray.subtlest")
});

const selectedButtonStyle = css({
    border: `2px solid ${token("color.link")}`,
    margin: 0
});

const cardTitleStyle = css({
    fontFamily: "var(--bxp-charlie-text-font-family)",
    color: token("color.text"),
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "20px" /* 125% */,
    margin: "0 0 5px 0",
    [media.above.md]: {
        fontSize: "20px",
        lineHeight: "28px"
    }
});

const subtextStyle = css({
    fontSize: "12px",
    fontFamily: "var(--bxp-charlie-text-font-family)",
    color: "#101214",
    fontWeight: 400,
    lineHeight: "20px" /* 143% */,
    margin: 0,
    [media.above.md]: {
        fontSize: "16px",
        lineHeight: "24px"
    }
});

const textContainerStyle = css({
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    padding: token("space.200"),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    textAlign: "start",
    [media.above.md]: {
        padding: `${token("space.300")} ${token("space.300")}`,
        justifyContent: "flex-start",
        flexGrow: 1
    }
});

const imgStyle = css({
    display: "none",
    [media.above.md]: {
        display: "block",
        width: "100%",
        height: "100%",
        flexBasis: "0"
    }
});

const mobileImgStyle = css({
    width: "80px",
    height: "100px",
    [media.above.md]: {
        display: "none"
    }
});

const desktopImageContainer = css({
    display: "none",
    [media.above.md]: {
        display: "block",
        maxHeight: "200px",
        minHeight: "174px"
    }
});

const Card: React.FC<CardProps> = ({
    imageSrc,
    mobileImgSrc,
    title,
    subtext,
    setSelectedTemplate,
    templateType,
    templateId,
    setSelectedTemplateId,
    setSelectedCardTitle,
    selectedCardTitle,
    teamType,
    templateMetadata,
    setSelectedTemplateMetadata
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    return (
        <button
            css={[
                cardBorderStyle,
                selectedCardTitle === title
                    ? selectedButtonStyle
                    : unselectedButtonStyle
            ]}
            onClick={() => {
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId:
                        TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceCard,
                    attributes: {
                        atlasTicket: "ATLAS-61649",
                        teamType,
                        templateType,
                        templateId,
                        templateName: title
                    }
                }).fire();

                setSelectedTemplate(templateType);
                setSelectedTemplateId(templateId);
                setSelectedCardTitle(title);
                setSelectedTemplateMetadata(templateMetadata);
            }}
        >
            <div css={desktopImageContainer}>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <img src={imageSrc} alt={title} css={imgStyle} />
            </div>
            {/* eslint-disable-next-line no-restricted-syntax */}
            <img src={mobileImgSrc} alt={title} css={mobileImgStyle} />
            <div css={textContainerStyle}>
                <h3 css={cardTitleStyle}>{title}</h3>
                <p css={subtextStyle}>{subtext}</p>
            </div>
        </button>
    );
};

export default Card;
